import * as yup from 'yup'

import {
  MAX_MULTI_LINE_CHAR_COUNT,
  MAX_SINGLE_LINE_CHAR_COUNT,
  PWD_MIN_LENGTH,
  REGEX_PATTERN_LOWER_CASE,
  REGEX_PATTERN_NO_NUMBERS,
  REGEX_PATTERN_NUMBER,
  REGEX_PATTERN_PWD_SPECIAL_CHAR,
  REGEX_PATTERN_UPPER_CASE,
} from './constants'
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js';

import { t } from 'i18next'

/**
 * Validation schema definitions for all forms used in the application.
 *
 * The schemas utilize Yup validation methods and leverage i18next for localized error messages.
 */

// RegisterProfileScreen Validation
export const adminSchema = yup.object().shape({
  firstName: yup.string().trim()
    .max(MAX_SINGLE_LINE_CHAR_COUNT, t('charCountExceed', {
      count: MAX_SINGLE_LINE_CHAR_COUNT,
      field: t('firstName')
    }))
    .matches(REGEX_PATTERN_NO_NUMBERS, t('numberNotAllowed'))
    .required(t('fieldRequired', { field: t('firstName') })),
  lastName: yup.string().trim()
    .max(MAX_SINGLE_LINE_CHAR_COUNT, t('charCountExceed', {
      count: MAX_SINGLE_LINE_CHAR_COUNT,
      field: t('lastName')
    }))
    .matches(REGEX_PATTERN_NO_NUMBERS, t('numberNotAllowed'))
    .required(t('fieldRequired', { field: t('lastName') })),
  loginId: yup
    .string()
    .trim()
    .required(t('fieldRequired', { field: t('email') }))
    .email(t('fieldInvalid', { field: t('email') }))
})

export const OrgDetailSchema = yup.object().shape({
  name: yup.string().trim()
    .required(t('orgNameRequired'))
    .max(
      MAX_SINGLE_LINE_CHAR_COUNT,
      t('charCountExceed', {
        count: MAX_SINGLE_LINE_CHAR_COUNT,
        field: t('name')
      })
    ),
  licenceId: yup.number()
    .required(t('licenceRequired')
    ),
  epicorCustomerId: yup.string().trim()
    .nullable()
    .max(
      MAX_SINGLE_LINE_CHAR_COUNT,
      t('charCountExceed', {
        count: MAX_SINGLE_LINE_CHAR_COUNT,
        field: t('epicorCustomerId')
      })
    ),
  epicorSalesOrder: yup.string().trim()
    .nullable()
    .max(
      MAX_SINGLE_LINE_CHAR_COUNT,
      t('charCountExceed', {
        count: MAX_SINGLE_LINE_CHAR_COUNT,
        field: t('epicorSalesOrder')
      })
    ),
  supportEmail: yup.string().trim()
    .required(t('fieldRequired', { field: t('email') }))
    .email(t('fieldInvalid', { field: t('email') })),
  description: yup.string().trim()
    .max(MAX_MULTI_LINE_CHAR_COUNT, t('charCountExceed', {
      count: MAX_MULTI_LINE_CHAR_COUNT,
      field: t('description')
    })),
  pinCode: yup.string().trim()
    .nullable()
    .test('len', t('pinValidation', { value: 4 }),
    val => {
      if (!val) return true; // allow empty or null
      const parsed = parseInt(val, 10);
      return parsed >= 1000 && parsed <= 9999;
    }),
	organizationTelephonyCountryCode: yup.string()
		.trim()
    .nullable()
		//.required(t('fieldRequired', { field: t('code') }))
		.test('validCountryCode', t('invalidField', { field: t('code') }), val => {
			if (!val) return true;
			return validCallingCodes.includes(val as any);
		}),

	organizationTelephonyNumber: yup.string()
		.trim()
    .nullable()    
		//.required(t('fieldRequired', { field: t('telephonyNumber') }))
		.test('validPhone', t('invalidField', { field: t('telephonyNumber') }), function (val) {
			const { organizationTelephonyCountryCode } = this.parent;
			if (!val || !organizationTelephonyCountryCode) return true;
			try {
				let sanitized = val;
				if (sanitized.startsWith('00')) sanitized = sanitized.substring(2);
				else if (sanitized.startsWith('0')) sanitized = sanitized.substring(1);
				return isValidPhoneNumber(`+${organizationTelephonyCountryCode}${sanitized}`);
			} catch {
				return false;
			}
		}),
  messageTypeEnabled: yup.boolean(),
  priorityEnabled: yup.boolean()
});

export const PasswordSchema = yup.object({
  password: yup.string()
    .required()
    .min(PWD_MIN_LENGTH)
    .matches(REGEX_PATTERN_LOWER_CASE)
    .matches(REGEX_PATTERN_UPPER_CASE)
    .matches(REGEX_PATTERN_NUMBER)
    .matches(REGEX_PATTERN_PWD_SPECIAL_CHAR)
});

// Build valid codes once, outside schema
const validCallingCodes = getCountries().map(country =>
	getCountryCallingCode(country)
);

export const whiteListSchema = yup.object().shape({
	countryCode: yup.string()
		.trim()
    .nullable()
		//.required(t('fieldRequired', { field: t('code') }))
		.test('validCountryCode', t('invalidField', { field: t('code') }), val => {
			if (!val) return true;
			return validCallingCodes.includes(val as any);
		}),

	phone: yup.string()
		.trim()
    .nullable()
		//.required(t('fieldRequired', { field: t('number') }))
		.test('validPhone', t('invalidField', { field: t('number') }), function (val) {
			const { countryCode } = this.parent;
			if (!val || !countryCode) return true;
			try {
				// Strip 00 or 0 prefix
				let sanitized = val;
				if (sanitized.startsWith('00')) {
					sanitized = sanitized.substring(2);
				} else if (sanitized.startsWith('0')) {
					sanitized = sanitized.substring(1);
				}

				const full = `+${countryCode}${sanitized}`;
				return isValidPhoneNumber(full);
			} catch {
				return false;
			}
		}),
});
